import React, { useState } from 'react';

import {
  Typography,
  Container,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TablePagination,
  TableSortLabel
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';

import { Translate, SafeAnchor } from '@hydra/common/components';
import QuerySearch from '@hydra/routes/keywords/components/QuerySearch';
import QueryFilter from '@hydra/routes/keywords/components/QueryFilter';

import { useI18n } from '@hydra/common/customHooks';

const INITIAL_PAGE = 0;
const INITIAL_ROWS_PER_PAGE = 5;

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2.5,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3)
    }
  },
  table: {
    tableLayout: 'fixed'
  },
  disablePadding: {
    paddingLeft: 0,
    paddingRight: 0
  },
  skeletonLeft: {
    marginLeft: 'auto'
  },
  skeletonRight: {
    marginRight: 'auto'
  },
  queryCell: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  url: {
    fontSize: theme.typography.fontSizeSmall
  },
  rightToolbar: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  tableRowHeight: {
    height: '60px'
  },
  emptyWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60px',
    padding: theme.spacing(0, 1.75)
  }
}));

interface TableQueries {
  data?: {
    queries: Query[];
    gwtSite: string | null;
    gwtAccountEmail: string | null;
  };
  url: string;
  isLoading: boolean;
  hasError: boolean;
}

interface Query {
  query: string
  pattern: string
  url: string
  searchAnalytics: {
    clicks: number
    impressions: number
    ctr: number
    position: number
  }
}

interface MappedQuery {
  query: string
  pattern: string
  url: string
  clicks: number
  impressions: number
  ctr: number
  position: number
}

type Order = 'desc' | 'asc'
type OrderBy = 'query' | 'pattern' | 'clicks' | 'impressions' | 'ctr' | 'position'
type HeadCell = {
  id: OrderBy
  numeric: boolean
  disablePadding: boolean
  label: string
  width: string
}

const headCells: HeadCell[] = [
  { id: 'query', numeric: false, disablePadding: false, label: 'gsc_keywords_query', width: '55%' },
  { id: 'pattern', numeric: false, disablePadding: true, label: 'gsc_keywords_question', width: '8%' },
  { id: 'clicks', numeric: true, disablePadding: true, label: 'gsc_keywords_clicks', width: '8%' },
  { id: 'impressions', numeric: true, disablePadding: true, label: 'gsc_keywords_impressions', width: '11%' },
  { id: 'ctr', numeric: true, disablePadding: true, label: 'gsc_keywords_ctr', width: '8%' },
  { id: 'position', numeric: true, disablePadding: true, label: 'gsc_keywords_position', width: '10%' }
];

function InitialLoadingTable ({ rowsPerPage }: { rowsPerPage: number }) {
  const classes = useStyles();
  return (
    <>
      {new Array(rowsPerPage).fill(null).map((n, i) => (
        <TableRow key={i} className={classes.tableRowHeight}>
          <TableCell component='th' scope='row' width='55%'>
            <Skeleton width='60%' />
            <Skeleton />
          </TableCell>
          <TableCell className={classes.disablePadding} component='th' scope='row' width='8%'>
            <Skeleton width='50%' className={classes.skeletonRight} />
          </TableCell>
          <TableCell className={classes.disablePadding} component='th' scope='row' width='8%'>
            <Skeleton width='35%' className={classes.skeletonLeft} />
          </TableCell>
          <TableCell className={classes.disablePadding} component='th' scope='row' width='11%'>
            <Skeleton width='35%' className={classes.skeletonLeft} />
          </TableCell>
          <TableCell className={classes.disablePadding} component='th' scope='row' width='8%'>
            <Skeleton width='40%' className={classes.skeletonLeft} />
          </TableCell>
          <TableCell className={classes.disablePadding} component='th' scope='row' width='10%'>
            <Skeleton width='35%' className={classes.skeletonLeft} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function Header ({ order, orderBy, onRequestSort }: { order: Order, orderBy: OrderBy, onRequestSort: any }) {
  const classes = useStyles();

  const createSortHandler = (property: OrderBy) => (event: any) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell: HeadCell) => (
          <TableCell
            width={headCell.width}
            key={headCell.id}
            className={headCell.disablePadding ? classes.disablePadding : ''}
            align={headCell.numeric ? 'right' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              <Translate content={headCell.label} />
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function descendingComparator (a: MappedQuery, b: MappedQuery, orderBy: OrderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator (order: Order, orderBy: OrderBy): (a: MappedQuery, b: MappedQuery) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort (array: MappedQuery[], comparator: any): MappedQuery[] {
  const stabilizedThis: [MappedQuery, number][] = array.map((el: MappedQuery, index: number) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el: [MappedQuery, number]) => el[0]);
}

export default function TableQueries ({ data, isLoading, hasError, url }: TableQueries) {
  const queries = data && data.queries ? data.queries : [];
  const mappedQueries: MappedQuery[] = queries.map((query: Query) => {
    return {
      query: query.query,
      url: query.url,
      pattern: query.pattern,
      clicks: query.searchAnalytics.clicks,
      impressions: query.searchAnalytics.impressions,
      ctr: query.searchAnalytics.ctr,
      position: query.searchAnalytics.position
    };
  });
  const classes = useStyles();
  const { numeral, translate } = useI18n();

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('clicks');
  const [page, setPage] = useState(INITIAL_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(INITIAL_ROWS_PER_PAGE);

  const [querySearch, setQuerySearch] = useState('');
  const [clicksFilter, setClicksFilter] = useState({ from: '', to: '' });
  const [impressionsFilter, setImpressionsFilter] = useState({ from: '', to: '' });
  const [ctrFilter, setCtrFilter] = useState({ from: '', to: '' });
  const [positionFilter, setPositionFilter] = useState({ from: '', to: '' });

  const handleRequestSort = (event: any, property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const labelDisplayedRows = ({ from, to, count }: { from: number, to: number, count: number }): string => {
    return `${from}-${to === -1 ? count : to} ${translate('gsc_keywords_table_of')} ${count}`;
  };

  const filterQueries = (arr: MappedQuery[]) => {
    let result: MappedQuery[] = arr.map(x => x);
    if (querySearch) {
      result = result.filter(({ query }) => query.toLowerCase().includes(querySearch.toLowerCase()));
    }
    if (clicksFilter.from || clicksFilter.to) {
      const { from, to } = clicksFilter;
      result = result.filter(({ clicks }) => {
        if (from && to) {
          return parseFloat(from) <= clicks && clicks <= parseFloat(to);
        } else if (from) {
          return parseFloat(from) <= clicks;
        } else {
          return clicks <= parseFloat(to);
        }
      });
    }
    if (impressionsFilter.from || impressionsFilter.to) {
      const { from, to } = impressionsFilter;
      result = result.filter(({ impressions }) => {
        if (from && to) {
          return parseFloat(from) <= impressions && impressions <= parseFloat(to);
        } else if (from) {
          return parseFloat(from) <= impressions;
        } else {
          return impressions <= parseFloat(to);
        }
      });
    }
    if (positionFilter.from || positionFilter.to) {
      const { from, to } = positionFilter;
      result = result.filter(({ position }) => {
        if (from && to) {
          return parseFloat(from) <= position && position <= parseFloat(to);
        } else if (from) {
          return parseFloat(from) <= position;
        } else {
          return position <= parseFloat(to);
        }
      });
    }
    if (ctrFilter.from || ctrFilter.to) {
      const { from, to } = ctrFilter;
      result = result.filter(({ ctr }) => {
        if (from && to) {
          return parseFloat(from) <= ctr && ctr <= parseFloat(to);
        } else if (from) {
          return parseFloat(from) <= ctr;
        } else {
          return ctr <= parseFloat(to);
        }
      });
    }

    return result;
  };

  const filteredQueries = filterQueries(mappedQueries);

  const showPagination = data && data.gwtSite && filteredQueries.length > INITIAL_ROWS_PER_PAGE;
  const showEmptyFilterState = data && data.gwtSite && data.queries.length > 0 && filteredQueries.length === 0;
  const showErrorState = !isLoading && hasError;
  const showNoResults = data && data.gwtSite && data.queries.length === 0;

  return (
    <Container className={classes.root}>
      <div className={classes.rightToolbar}>
        <QuerySearch
          querySearch={querySearch}
          setQuerySearch={setQuerySearch}
        />
        <QueryFilter
          clicksFilter={clicksFilter}
          setClicksFilter={setClicksFilter}
          impressionsFilter={impressionsFilter}
          setImpressionsFilter={setImpressionsFilter}
          positionFilter={positionFilter}
          setPositionFilter={setPositionFilter}
          ctrFilter={ctrFilter}
          setCtrFilter={setCtrFilter}
        />
      </div>
      <Table className={classes.table} size='small'>
        <Header
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
        />
        <TableBody>
          {isLoading
            ? <InitialLoadingTable rowsPerPage={rowsPerPage} />
            : (
              <>
                {
                  stableSort(filteredQueries, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((query: MappedQuery) => {
                      return (
                        <TableRow key={query.query} className={classes.tableRowHeight}>
                          <TableCell align='left' width='55%' className={classes.queryCell}>
                            <Typography noWrap component='span'>
                              <Typography noWrap component='div'>
                                {query.query}
                              </Typography>
                              <SafeAnchor
                                href={query.url}
                                rel='nofollow noopener noreferrer'
                                target='_blank'
                              >
                                <Typography noWrap component='span' className={classes.url}>
                                  {query.url}
                                </Typography>
                              </SafeAnchor>
                            </Typography>
                          </TableCell>
                          <TableCell align='left' width='8%' className={classes.disablePadding}>
                            <Translate content={`gsc_keywords_question_${query.pattern}`} />
                          </TableCell>
                          <TableCell align='right' width='8%' className={classes.disablePadding}>
                            {numeral(query.clicks).format()}
                          </TableCell>
                          <TableCell align='right' width='11%' className={classes.disablePadding}>
                            {numeral(query.impressions).format()}
                          </TableCell>
                          <TableCell align='right' width='8%' className={classes.disablePadding}>
                            {numeral(query.ctr).format('0.0%')}
                          </TableCell>
                          <TableCell align='right' width='10%' className={classes.disablePadding}>
                            {numeral(query.position).format()}
                          </TableCell>
                        </TableRow>
                      );
                    })
                }
              </>
              )}
        </TableBody>
      </Table>
      {showPagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={filteredQueries.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage=''
          labelDisplayedRows={labelDisplayedRows}
        />
      )}
      {showEmptyFilterState && (
        <div className={classes.emptyWrapper}>
          <Translate content='gsc_keywords_filter_empty_state' />
        </div>
      )}
      {showErrorState && (
        <div className={classes.emptyWrapper}>
          <Translate content='error_oops' />
        </div>
      )}
      {showNoResults && (
        <div className={classes.emptyWrapper}>
          <Translate content='gsc_keywords_no_queries_found' />
        </div>
      )}
    </Container>
  );
}
